import "../styles/EditTask.css";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { taskService } from "../../services/taskService";

// Tiptap і розширення
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import TextStyle from "@tiptap/extension-text-style";
import ListItem from "@tiptap/extension-list-item";
import Color from "@tiptap/extension-color";
import Link from "@tiptap/extension-link";
import ResizeImage from "tiptap-extension-resize-image";

// Компоненти інтерфейсу і іконки
import { Button, Tooltip, Menu, ActionIcon, Group, Paper, Title, Container, Loader, Center } from "@mantine/core";
import { notifications } from '@mantine/notifications';
import {
    IconBold,
    IconItalic,
    IconUnderline,
    IconStrikethrough,
    IconCode,
    IconList,
    IconListNumbers,
    IconAlignLeft,
    IconAlignCenter,
    IconAlignRight,
    IconAlignJustified,
    IconH1,
    IconH2,
    IconH3,
    IconArrowBackUp,
    IconArrowForwardUp,
    IconChevronDown
} from "@tabler/icons-react";

const EditTask = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [task, setTask] = useState(null);
    const [loading, setLoading] = useState(true);

    // Завантажуємо задачу з сервера
    useEffect(() => {
        const fetchTask = async () => {
            try {
                const response = await taskService.getTaskById(id);
                setTask(response.task);
                setLoading(false);
            } catch (error) {
                console.error("Error fetching task:", error);
                notifications.show({
                    title: 'Error',
                    message: 'Не вдалося завантажити задачу.',
                    color: 'red'
                });
                setLoading(false);
            }
        };
        fetchTask();
    }, [id]);

    // Ініціалізуємо редактор Tiptap
    const editor = useEditor({
        extensions: [
            StarterKit,
            Underline,
            TextAlign.configure({ types: ["heading", "paragraph"] }),
            TextStyle.configure({ types: [ListItem.name] }),
            Color.configure({ types: [TextStyle.name, ListItem.name] }),
            Link.configure({ openOnClick: false }),
            ResizeImage.configure({
                allowBase64: true
            })
        ],
        // ✅ ДОДАНО ОБРОБНИК ВСТАВКИ ЗОБРАЖЕНЬ
        editorProps: {
            handlePaste: (view, event) => {
                const files = event.clipboardData?.files;

                if (!files || files.length === 0) {
                    return false;
                }

                const imageFiles = Array.from(files).filter((file) =>
                    file.type.startsWith("image/")
                );

                if (imageFiles.length === 0) {
                    return false;
                }

                event.preventDefault();

                imageFiles.forEach((file) => {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                        const src = e.target.result;
                        editor.chain().focus().setImage({ src }).run();
                    };
                    reader.readAsDataURL(file);
                });

                return true;
            }
        },
        content: ""
    });

    // Оновлюємо контент редактора після завантаження задачі
    useEffect(() => {
        // Перевіряємо, що редактор готовий, є дані, і що поточний вміст не співпадає з тим, що ми хочемо вставити
        // Це запобігає зайвим оновленням і втраті позиції курсора
        if (editor && task?.content && editor.getHTML() !== task.content) {
            editor.commands.setContent(task.content);
        }
    }, [task, editor]);

    // Функція для застосування заголовка
    const applyHeading = (level) => {
        if (!editor) return;
        const { state } = editor;
        const { selection } = state;
        if (selection.empty) {
            editor
                .chain()
                .focus()
                .splitBlock()
                .setNode("heading", { level })
                .run();
        } else {
            editor.chain().focus().toggleHeading({ level }).run();
        }
    };

    // Збереження змін
    const saveTask = async () => {
        if (!editor) return;
        try {
            await taskService.updateTask(id, {
                title: task.title,
                content: editor.getHTML()
            });
            notifications.show({
                title: 'Success',
                message: 'Зміни збережено!',
                color: 'green'
            });
            navigate("/tasker");
        } catch (error) {
            console.error("Error saving task:", error);
            notifications.show({
                title: 'Error',
                message: 'Не вдалося зберегти зміни.',
                color: 'red'
            });
        }
    };

    if (loading) return <Center h={200}><Loader /></Center>;
    if (!task) return <p>Задача не знайдена.</p>;

    return (
        <Container size="xl">
            <Title order={2} mb="md">Редагування: {task.title}</Title>

            {editor && (
                <Paper withBorder p="xs" mb="sm" radius="md">
                    <Group gap={5}>
                        {/* Заголовки (Menu) */}
                        <Menu shadow="md" width={200}>
                            <Menu.Target>
                                <Button variant="subtle" size="xs" rightSection={<IconChevronDown size={14} />}>
                                    Heading
                                </Button>
                            </Menu.Target>

                            <Menu.Dropdown>
                                <Menu.Item leftSection={<IconH1 size={14} />} onClick={() => applyHeading(1)}>
                                    Heading 1
                                </Menu.Item>
                                <Menu.Item leftSection={<IconH2 size={14} />} onClick={() => applyHeading(2)}>
                                    Heading 2
                                </Menu.Item>
                                <Menu.Item leftSection={<IconH3 size={14} />} onClick={() => applyHeading(3)}>
                                    Heading 3
                                </Menu.Item>
                            </Menu.Dropdown>
                        </Menu>

                        {/* Основні стилі */}
                        <Tooltip label="Bold">
                            <ActionIcon variant="subtle" onClick={() => editor.chain().focus().toggleBold().run()}>
                                <IconBold size={18} />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Italic">
                            <ActionIcon variant="subtle" onClick={() => editor.chain().focus().toggleItalic().run()}>
                                <IconItalic size={18} />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Underline">
                            <ActionIcon variant="subtle" onClick={() => editor.chain().focus().toggleUnderline().run()}>
                                <IconUnderline size={18} />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Strikethrough">
                            <ActionIcon variant="subtle" onClick={() => editor.chain().focus().toggleStrike().run()}>
                                <IconStrikethrough size={18} />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Code">
                            <ActionIcon variant="subtle" onClick={() => editor.chain().focus().toggleCode().run()}>
                                <IconCode size={18} />
                            </ActionIcon>
                        </Tooltip>

                        {/* Списки */}
                        <Tooltip label="Bullet list">
                            <ActionIcon variant="subtle" onClick={() => editor.chain().focus().toggleBulletList().run()}>
                                <IconList size={18} />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Ordered list">
                            <ActionIcon variant="subtle" onClick={() => editor.chain().focus().toggleOrderedList().run()}>
                                <IconListNumbers size={18} />
                            </ActionIcon>
                        </Tooltip>

                        {/* Вирівнювання */}
                        <Tooltip label="Align Left">
                            <ActionIcon variant="subtle" onClick={() => editor.chain().focus().setTextAlign("left").run()}>
                                <IconAlignLeft size={18} />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Align Center">
                            <ActionIcon variant="subtle" onClick={() => editor.chain().focus().setTextAlign("center").run()}>
                                <IconAlignCenter size={18} />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Align Right">
                            <ActionIcon variant="subtle" onClick={() => editor.chain().focus().setTextAlign("right").run()}>
                                <IconAlignRight size={18} />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Justify">
                            <ActionIcon variant="subtle" onClick={() => editor.chain().focus().setTextAlign("justify").run()}>
                                <IconAlignJustified size={18} />
                            </ActionIcon>
                        </Tooltip>

                        {/* Undo / Redo */}
                        <Tooltip label="Undo">
                            <ActionIcon variant="subtle" onClick={() => editor.chain().focus().undo().run()}>
                                <IconArrowBackUp size={18} />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Redo">
                            <ActionIcon variant="subtle" onClick={() => editor.chain().focus().redo().run()}>
                                <IconArrowForwardUp size={18} />
                            </ActionIcon>
                        </Tooltip>
                    </Group>
                </Paper>
            )}

            {/* Вміст редактора */}
            {editor && (
                <Paper withBorder p="md" radius="md" style={{ minHeight: '300px' }}>
                    <EditorContent editor={editor} />
                </Paper>
            )}

            {/* Кнопка збереження */}
            <Button mt="md" onClick={saveTask}>
                Зберегти зміни
            </Button>
        </Container>
    );
};

export default EditTask;
